import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Edit2, Mail } from 'lucide-react';
import { usePerm } from '@/hooks/use-perm';
import { useUpdateBillingEmail } from '../hooks/use-billing-mutations';
import { useAccountPlan } from '../hooks/use-billing-data';

interface BillingEmailSectionProps {
  accountPlan: any;
}

export function BillingEmailSection({ accountPlan }: BillingEmailSectionProps) {
  const { t } = useTranslation('billing');
  const { isAdmin } = usePerm();
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const { refetchAccountPlan } = useAccountPlan();
  const updateBillingEmailMutation = useUpdateBillingEmail(() => {
    setIsEditingEmail(false);
    refetchAccountPlan();
  });

  useEffect(() => {
    if (accountPlan?.billingEmail) {
      setEditedEmail(accountPlan.billingEmail);
    }
  }, [accountPlan?.billingEmail]);

  const handleSaveBillingEmail = () => {
    if (!editedEmail) {
      setEmailError(t('billingEmail.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedEmail)) {
      setEmailError(t('billingEmail.invalidEmail'));
      return;
    }
    setEmailError('');
    updateBillingEmailMutation.mutate({ email: editedEmail });
  };

  const handleCancelEmailEdit = () => {
    setIsEditingEmail(false);
    setEditedEmail(accountPlan?.billingEmail || '');
    setEmailError('');
  };

  return (
    <div className="mt-8 border-t pt-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">{t('billingEmail.title')}</h3>
          <p className="text-muted-foreground text-sm">{t('billingEmail.description')}</p>
        </div>
      </div>
      {isEditingEmail ? (
        <div className="space-y-3">
          <div>
            <Input
              type="email"
              value={editedEmail}
              onChange={(e) => {
                setEditedEmail(e.target.value);
                setEmailError('');
              }}
              className={emailError ? 'border-red-500' : ''}
              placeholder={t('billingEmail.placeholder')}
              disabled={updateBillingEmailMutation.isPending}
            />
            {emailError && (
              <p className="mt-1 text-sm text-red-600">{emailError}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSaveBillingEmail}
              disabled={updateBillingEmailMutation.isPending}
              size="sm"
            >
              {updateBillingEmailMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
            <Button
              onClick={handleCancelEmailEdit}
              disabled={updateBillingEmailMutation.isPending}
              size="sm"
              variant="outline"
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-muted flex items-center justify-between rounded-lg border p-4">
          <div className="flex items-center space-x-3">
            <Mail className="text-muted-foreground h-5 w-5" />
            <div>
              <p className="text-sm font-medium">
                {accountPlan?.billingEmail || t('billingEmail.notSet')}
              </p>
              <p className="text-muted-foreground text-xs">
                {accountPlan?.billingEmail ? t('billingEmail.hintSet') : t('billingEmail.hintUnset')}
              </p>
            </div>
          </div>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setIsEditingEmail(true)}>
              <Edit2 className="mr-1 h-4 w-4" />
              {t('common.edit')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
